<template>
  <div class="d-flex flex-column-fluid silence-approval-page">
    <div class="container">
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="header-title">
            <h1 class="title">Approve silence</h1>
            <p class="subtitle">Confirm silencing this financing alert</p>
          </div>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner-border" role="status"></div>
        <div class="loading-text">Loading alert...</div>
      </div>

      <div v-else-if="error" class="result-card error-card">
        <i class="las la-exclamation-circle result-icon"></i>
        <h2 class="result-title">Unable to approve silence</h2>
        <p class="result-message">{{ error }}</p>
      </div>

      <div v-else-if="isConfirmed" class="result-card success-card">
        <i class="las la-check result-icon"></i>
        <h2 class="result-title">Silence approved</h2>
        <p class="result-message">
          This alert has been silenced and will no longer trigger notifications.
        </p>
      </div>

      <div v-else-if="breach" class="approval-content">
        <div class="breach-card">
          <div class="card-header">
            <h5 class="card-title">Alert details</h5>
            <span :class="['status-badge', statusBadgeClass]">
              {{ statusLabel }}
            </span>
          </div>
          <div class="card-body">
            <div class="details-grid">
              <div class="detail-item">
                <label>Rule</label>
                <div class="value">{{ breach.rule_name || '—' }}</div>
              </div>
              <div class="detail-item">
                <label>Business</label>
                <div class="value">{{ breach.business_name || '—' }}</div>
              </div>
              <div class="detail-item">
                <label>Occurrences</label>
                <div class="value">{{ occurrenceLabel }}</div>
              </div>
              <div class="detail-item">
                <label>First detected</label>
                <div class="value">{{ formatDate(breach.first_detected_at) }}</div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="actionError" class="alert alert-danger action-error">
          <i class="material-icons">error</i>
          {{ actionError }}
        </div>

        <button
          type="button"
          class="btn btn-approve"
          :disabled="approving"
          @click="approveSilence"
        >
          <span v-if="approving" class="spinner-border spinner-border-sm mr-2"></span>
          <i v-else class="las la-bell-slash"></i>
          {{ approving ? 'Approving...' : 'Approve silence' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import RulesService from "@/services/rules.service";
import { formatErrorMessage } from "@/utils/random_utils";

const AWAITING_STATUS = "awaiting_silencing_approval";
const SILENCED_STATUS = "silenced";
const NOT_AWAITING_MESSAGE = "This alert is not waiting for silence approval";
const NOT_FOUND_MESSAGE = "Breach not found";

export default {
  name: "SilenceApproval",
  data() {
    return {
      loading: true,
      approving: false,
      error: null,
      actionError: null,
      breach: null,
      confirmed: false
    };
  },
  computed: {
    isConfirmed() {
      return this.confirmed || this.getStatus(this.breach) === SILENCED_STATUS;
    },
    statusLabel() {
      const status = this.getStatus(this.breach);
      if (status === AWAITING_STATUS) return "Awaiting silence approval";
      if (status === SILENCED_STATUS) return "Silenced";
      return status || "—";
    },
    statusBadgeClass() {
      const status = this.getStatus(this.breach);
      if (status === AWAITING_STATUS) return "status-awaiting";
      if (status === SILENCED_STATUS) return "status-silenced";
      return "status-default";
    },
    occurrenceLabel() {
      if (!this.breach || this.breach.occurrence_count === undefined || this.breach.occurrence_count === null) {
        return "—";
      }
      return this.breach.occurrence_count;
    }
  },
  mounted() {
    this.loadBreach();
  },
  methods: {
    async loadBreach() {
      const breachId = this.$route.params.breachId;
      this.loading = true;
      this.error = null;
      this.actionError = null;
      this.confirmed = false;
      this.breach = null;

      if (!breachId) {
        this.error = NOT_FOUND_MESSAGE;
        this.loading = false;
        return;
      }

      try {
        const response = await RulesService.getBreach(breachId, {
          status: AWAITING_STATUS
        });
        this.applyLoadedBreach(this.unwrapBreach(response, breachId));
      } catch (error) {
        if (this.isAlreadySilenced(error)) {
          this.confirmed = true;
        } else if (this.isNotAwaiting(error)) {
          this.error = NOT_AWAITING_MESSAGE;
        } else {
          try {
            const fallback = await RulesService.getBreach(breachId);
            this.applyLoadedBreach(this.unwrapBreach(fallback, breachId));
          } catch (fallbackError) {
            try {
              const listResponse = await RulesService.getBreaches({
                id: breachId,
                status: AWAITING_STATUS
              });
              const fromList = this.unwrapBreach(listResponse, breachId);
              if (fromList) {
                this.applyLoadedBreach(fromList);
              } else {
                this.error = this.resolveLoadError(fallbackError);
              }
            } catch (listError) {
              this.error = this.resolveLoadError(fallbackError);
            }
          }
        }
      } finally {
        this.loading = false;
      }
    },

    applyLoadedBreach(breach) {
      if (!breach) {
        this.error = NOT_FOUND_MESSAGE;
        return;
      }

      this.breach = breach;
      const status = this.getStatus(breach);

      if (status === SILENCED_STATUS) {
        this.confirmed = true;
        return;
      }

      if (status !== AWAITING_STATUS) {
        this.error = NOT_AWAITING_MESSAGE;
      }
    },

    async approveSilence() {
      if (this.approving || !this.breach) return;

      this.approving = true;
      this.actionError = null;

      try {
        const response = await RulesService.approveSilence(this.breach.id || this.$route.params.breachId);
        const updated = this.unwrapBreach(response, this.$route.params.breachId) || this.breach;
        this.breach = Object.assign({}, this.breach, updated);

        if (this.getStatus(this.breach) === SILENCED_STATUS || this.isAlreadySilenced(response)) {
          this.confirmed = true;
          return;
        }

        this.confirmed = true;
      } catch (error) {
        if (this.isAlreadySilenced(error)) {
          this.confirmed = true;
          if (this.breach) {
            this.breach = Object.assign({}, this.breach, { status: SILENCED_STATUS });
          }
          return;
        }

        this.actionError = this.resolveApproveError(error);
      } finally {
        this.approving = false;
      }
    },

    unwrapBreach(response, breachId) {
      const payload = response && response.data !== undefined ? response.data : response;
      if (!payload) return null;

      if (Array.isArray(payload)) {
        return this.findBreach(payload, breachId);
      }

      if (Array.isArray(payload.results)) {
        return this.findBreach(payload.results, breachId);
      }

      if (Array.isArray(payload.data)) {
        return this.findBreach(payload.data, breachId);
      }

      if (payload.id || payload.rule_name || payload.status) {
        return payload;
      }

      return null;
    },

    findBreach(items, breachId) {
      if (!items || !items.length) return null;
      const match = items.find((item) => String(item.id) === String(breachId));
      return match || items[0];
    },

    getStatus(breach) {
      if (!breach || !breach.status) return "";
      return String(breach.status).trim().toLowerCase();
    },

    getErrorText(error) {
      if (!error) return "";
      if (typeof error === "string") return error;
      if (error.message) return String(error.message);
      return formatErrorMessage(error) || "";
    },

    getErrorStatus(error) {
      if (!error) return 0;
      if (error.status) return error.status;
      if (error.response && error.response.status) return error.response.status;
      return 0;
    },

    isNotFound(error) {
      const status = this.getErrorStatus(error);
      const text = this.getErrorText(error).toLowerCase();
      if (status === 404) return true;
      if (status === 400 && (text.includes("not found") || text.includes("does not exist"))) {
        return true;
      }
      return false;
    },

    isAlreadySilenced(source) {
      const status = this.getStatus(this.unwrapBreach(source, this.$route.params.breachId));
      if (status === SILENCED_STATUS) return true;

      const text = this.getErrorText(source).toLowerCase();
      return text.includes("already silenced") || text.includes("already been silenced");
    },

    isNotAwaiting(error) {
      const text = this.getErrorText(error).toLowerCase();
      return (
        text.includes("not waiting for silence approval") ||
        text.includes("not awaiting") ||
        text.includes("awaiting_silencing_approval")
      );
    },

    resolveLoadError(error) {
      if (this.isNotFound(error)) return NOT_FOUND_MESSAGE;
      if (this.isNotAwaiting(error)) return NOT_AWAITING_MESSAGE;
      return formatErrorMessage(error) || "Failed to load this alert. Please try again.";
    },

    resolveApproveError(error) {
      if (this.isNotFound(error)) return NOT_FOUND_MESSAGE;
      if (this.isNotAwaiting(error)) return NOT_AWAITING_MESSAGE;
      return formatErrorMessage(error) || "Failed to approve silence. Please try again.";
    },

    formatDate(dateString) {
      if (!dateString) return "—";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "—";
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    }
  }
};
</script>

<style scoped lang="scss">
@import './index.scss';

.silence-approval-page {
  .container {
    max-width: 760px;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1rem;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.loading-text {
  margin-top: 0.75rem;
  color: #64748b;
  font-size: 0.9375rem;
}

.result-card,
.breach-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.result-card {
  padding: 3rem 2rem;
  text-align: center;
}

.result-icon {
  font-size: 3rem;
  margin-bottom: 0.75rem;
}

.success-card .result-icon {
  color: #15803d;
}

.error-card .result-icon {
  color: #dc2626;
}

.result-title {
  margin: 0 0 0.5rem;
  font-size: 1.375rem;
  font-weight: 700;
  color: #0f172a;
}

.result-message {
  margin: 0;
  color: #64748b;
  font-size: 0.9375rem;
}

.approval-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.breach-card {
  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .card-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
  }

  .card-body {
    padding: 1.25rem;
  }
}

.details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.25rem;
}

.detail-item {
  label {
    display: block;
    margin-bottom: 0.25rem;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #64748b;
  }

  .value {
    font-size: 0.9375rem;
    color: #0f172a;
    font-weight: 600;
  }
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.status-awaiting {
  background: #fff7ed;
  color: #9a3412;
  border: 1px solid #fed7aa;
}

.status-silenced {
  background: #e2e3e5;
  color: #383d41;
  border: 1px solid #d6d8db;
}

.status-default {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.action-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
}

.btn-approve {
  align-self: flex-start;
  background: #111827;
  border: none;
  color: #fff;
  font-weight: 600;
  padding: 0.75rem 1.25rem;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;

  &:hover:not(:disabled) {
    background: #1f2937;
    color: #fff;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  i {
    font-size: 1.1rem;
  }
}

@media (max-width: 640px) {
  .details-grid {
    grid-template-columns: 1fr;
  }

  .btn-approve {
    width: 100%;
    justify-content: center;
  }
}
</style>
